<!-- ═══════════════════════════════════════════════════════════════════
     COMPONENTE: Anexos de Intercorrência
     Drag-and-drop upload → S3 → preview → reordenação → exclusão
     ═══════════════════════════════════════════════════════════════════ -->

<div class="space-y-4">

  <!-- ─── Zona de Upload (Drag-and-Drop) ─────────────────────────── -->
  <div *ngIf="!readOnly"
    class="border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer"
    [ngClass]="{
      'border-westetics-purple-800 bg-purple-50': isDragOver,
      'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100': !isDragOver
    }"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
    (click)="fileInput.click()">

    <input #fileInput
      type="file"
      [accept]="acceptedExtensions"
      multiple
      class="hidden"
      (change)="onFileSelected($event)">

    <div class="flex flex-col items-center gap-2">
      <div class="w-12 h-12 rounded-full flex items-center justify-center"
        [ngClass]="isDragOver ? 'bg-westetics-purple-800 text-white' : 'bg-gray-200 text-gray-500'">
        <fa-icon [icon]="faCloudUploadAlt" class="text-xl"></fa-icon>
      </div>
      <p class="text-sm font-medium" [ngClass]="isDragOver ? 'text-westetics-purple-800' : 'text-gray-700'">
        {{ isDragOver ? 'Solte os arquivos aqui' : 'Arraste arquivos ou clique para selecionar' }}
      </p>
      <p class="text-xs text-gray-500">
        Imagens, PDF, DOCX, PPT e Vídeos · Máximo 100 MB por arquivo
      </p>
    </div>
  </div>

  <!-- ─── Erros de Validação ─────────────────────────────────────── -->
  <div *ngIf="validationErrors.length > 0" class="space-y-1">
    <div *ngFor="let error of validationErrors"
      class="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      <fa-icon [icon]="faExclamationCircle" class="mt-0.5 flex-shrink-0"></fa-icon>
      <span>{{ error }}</span>
    </div>
  </div>

  <!-- ─── Arquivos em upload ─────────────────────────────────────── -->
  <div *ngIf="uploadingFiles.length > 0" class="space-y-2">
    <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Enviando</h4>

    <div *ngFor="let uf of uploadingFiles"
      class="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg">

      <!-- Preview / ícone -->
      <div class="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
        <img *ngIf="uf.previewUrl" [src]="uf.previewUrl" alt="" class="w-full h-full object-cover">
        <fa-icon *ngIf="!uf.previewUrl"
          [icon]="getFileIcon(uf.contentType)"
          [ngClass]="getFileIconColor(uf.contentType)"
          class="text-lg">
        </fa-icon>
      </div>

      <!-- Info + Progresso -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between mb-1">
          <p class="text-sm font-medium text-gray-800 truncate">{{ uf.fileName }}</p>
          <span class="text-xs text-gray-500 ml-2 flex-shrink-0">{{ formatFileSize(uf.fileSize) }}</span>
        </div>

        <!-- Barra de progresso -->
        <div class="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div class="h-full rounded-full transition-all duration-300"
            [ngClass]="getProgressColor(uf)"
            [style.width.%]="uf.progress">
          </div>
        </div>

        <!-- Status text -->
        <div class="flex items-center justify-between mt-1">
          <span class="text-xs" [ngClass]="{
            'text-gray-500': uf.status === 'pending' || uf.status === 'uploading',
            'text-blue-600': uf.status === 'registering',
            'text-green-600': uf.status === 'done',
            'text-red-600': uf.status === 'error'
          }">
            <span *ngIf="uf.status === 'pending'">Aguardando…</span>
            <span *ngIf="uf.status === 'uploading'">Enviando {{ uf.progress }}%</span>
            <span *ngIf="uf.status === 'registering'">Registrando…</span>
            <span *ngIf="uf.status === 'done'" class="flex items-center gap-1">
              <fa-icon [icon]="faCheck" class="text-xs"></fa-icon> Concluído
            </span>
            <span *ngIf="uf.status === 'error'">{{ uf.errorMessage || 'Erro no upload' }}</span>
          </span>

          <!-- Retry / Remove -->
          <div class="flex items-center gap-1">
            <button *ngIf="uf.status === 'error'"
              (click)="retryUpload(uf)"
              class="text-xs text-westetics-purple-800 hover:underline">
              Tentar novamente
            </button>
            <button *ngIf="uf.status === 'error'"
              (click)="removeFromUploading(uf)"
              class="text-xs text-gray-400 hover:text-red-500 ml-1">
              <fa-icon [icon]="faTimes"></fa-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ─── Galeria de Anexos (salvos) ─────────────────────────────── -->
  <div *ngIf="attachments.length > 0">
    <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
      Anexos ({{ attachments.length }})
    </h4>

    <div cdkDropList
      [cdkDropListData]="attachments"
      [cdkDropListDisabled]="readOnly"
      (cdkDropListDropped)="onReorder($event)"
      class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">

      <div *ngFor="let att of attachments; let i = index"
        cdkDrag
        class="group relative border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow">

        <!-- Drag handle -->
        <div *ngIf="!readOnly" cdkDragHandle
          class="absolute top-1 left-1 z-10 w-6 h-6 rounded flex items-center justify-center bg-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
          <fa-icon [icon]="faGripVertical" class="text-xs text-gray-500"></fa-icon>
        </div>

        <!-- Botão excluir -->
        <button type="button" *ngIf="!readOnly"
          (click)="deleteAttachment(att); $event.stopPropagation()"
          class="absolute top-1 right-1 z-10 w-6 h-6 rounded flex items-center justify-center bg-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 hover:text-red-600">
          <fa-icon [icon]="faTrash" class="text-xs"></fa-icon>
        </button>

        <!-- Área de preview -->
        <a [href]="att.file_url" target="_blank" rel="noopener noreferrer"
          class="block aspect-square">
          <!-- Preview de imagem -->
          <img *ngIf="isImage(att.content_type)"
            [src]="att.file_url"
            [alt]="att.file_name"
            class="w-full h-full object-cover">

          <!-- Ícone para outros formatos -->
          <div *ngIf="!isImage(att.content_type)"
            class="w-full h-full flex flex-col items-center justify-center p-2 bg-gray-50">
            <fa-icon [icon]="getFileIcon(att.content_type)"
              [ngClass]="getFileIconColor(att.content_type)"
              class="text-3xl mb-1">
            </fa-icon>
            <span class="text-[10px] text-gray-400 uppercase font-medium">
              {{ att.file_name.split('.').pop() }}
            </span>
          </div>
        </a>

        <!-- Rodapé com nome -->
        <div class="px-2 py-1.5 border-t border-gray-100">
          <p class="text-xs text-gray-700 truncate" [title]="att.file_name">
            {{ att.file_name }}
          </p>
          <p class="text-[10px] text-gray-400">
            {{ formatFileSize(att.file_size) }}
          </p>
        </div>

        <!-- CDK Drag Preview customizado -->
        <div *cdkDragPreview class="w-24 h-24 bg-white border-2 border-westetics-purple-800 rounded-lg shadow-lg flex items-center justify-center overflow-hidden">
          <img *ngIf="isImage(att.content_type)" [src]="att.file_url" class="w-full h-full object-cover">
          <fa-icon *ngIf="!isImage(att.content_type)" [icon]="getFileIcon(att.content_type)" class="text-2xl text-gray-400"></fa-icon>
        </div>

        <!-- CDK Drag Placeholder -->
        <div *cdkDragPlaceholder class="border-2 border-dashed border-westetics-purple-800 rounded-lg bg-purple-50 aspect-square"></div>
      </div>
    </div>
  </div>

</div>
